import { useCallback, useMemo } from 'react';
import { isNil } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import slugify from 'slugify';
import { CurrencyCode, Units } from 'common/types/format';
import { DEFAULT_CURRENCY, MILLIONS_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { CALENDAR_DATE_FORMAT } from 'dashboard409a/common/constants';
import { isFirmWaterfallCompany } from 'pages/Waterfall/components/CompanyList/CompanyList.utils';
import { ISO_DATE_FORMAT, MOMENT_DEFAULT_DATE_FORMAT, toString, worldCurrencies } from 'utilities';
import {
  SetWaterfallMeasurementDateOptions,
  UseWaterfallUrlStateParams,
  WaterfallUrlState,
} from './useWaterfallUrlState.props';
import {
  WATERFALL_ALLOWED_PARAMS,
  WATERFALL_CURRENCY_FORMAT,
  WATERFALL_CURRENCY_FORMAT_TO_UNITS,
  WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION,
  WATERFALL_QUERY_PARAM_CURRENCY_FORMAT,
  WATERFALL_QUERY_PARAM_EXIT_DATE,
  WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE,
  WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE,
  WATERFALL_QUERY_PARAM_FOREIGN_CURRENCY,
  WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE,
  WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_INPUT_CURRENCY,
  WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_OVERRIDDEN,
  WATERFALL_QUERY_PARAM_LESS_DEBT,
  WATERFALL_QUERY_PARAM_PLUS_CASH,
  WATERFALL_QUERY_PARAM_VIEW,
  WATERFALL_UNITS_TO_CURRENCY_FORMAT,
  WaterfallCurrencyFormat,
  WaterfallExchangeRateInputCurrency,
} from '../../constants';

export type { UseWaterfallUrlStateParams, WaterfallUrlState } from './useWaterfallUrlState.props';

const isWaterfallCurrencyFormat = (value: string): value is WaterfallCurrencyFormat =>
  Object.values(WATERFALL_CURRENCY_FORMAT).includes(value as WaterfallCurrencyFormat);

const CLEARED_FOREIGN_EXCHANGE_RATE_PARAMS = {
  [WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE]: undefined,
  [WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_OVERRIDDEN]: undefined,
  [WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_INPUT_CURRENCY]: undefined,
};

const CLEARED_CASH_AND_DEBT_OVERRIDE_PARAMS = {
  [WATERFALL_QUERY_PARAM_PLUS_CASH]: undefined,
  [WATERFALL_QUERY_PARAM_LESS_DEBT]: undefined,
};

export const useWaterfallUrlState = ({
  isFirmPage,
  companyList,
  capTableList,
  companyMeasurementDates,
}: UseWaterfallUrlStateParams): WaterfallUrlState => {
  const navigate = useNavigate();
  const location = useLocation();

  const parsedQuery = useMemo(() => queryString.parse(location.search), [location.search]);

  // ============================================================
  // URL Update Helper
  // ============================================================
  const updateUrlParams = useCallback(
    (params: Record<string, string | undefined>, replace = true) => {
      // Read URL directly to avoid stale closure issues with multiple hook instances
      const freshParsedQuery = queryString.parse(window.location.search);

      // Convert to simple string record (take first value if array)
      const currentQuery: Record<string, string | undefined> = {};
      Object.entries(freshParsedQuery).forEach(([key, value]) => {
        if (typeof value === 'string') {
          currentQuery[key] = value;
        } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
          // What is written below reads better
          // eslint-disable-next-line prefer-destructuring
          currentQuery[key] = value[0];
        }
      });

      const newQuery: Record<string, string | undefined> = { ...currentQuery, ...params };

      // Remove undefined/null/empty values
      Object.keys(newQuery).forEach(key => {
        if (newQuery[key] === null || newQuery[key] === undefined || newQuery[key] === '') {
          delete newQuery[key];
        }
      });

      // For non-firm pages, filter to only allowed params
      if (!isFirmPage) {
        Object.keys(newQuery).forEach(key => {
          if (!WATERFALL_ALLOWED_PARAMS.includes(key)) {
            delete newQuery[key];
          }
        });
      }

      const search = Object.keys(newQuery).length > 0 ? `?${queryString.stringify(newQuery)}` : '';

      navigate({ pathname: location.pathname, search }, { replace });
    },
    [navigate, location.pathname, isFirmPage]
  );

  // ============================================================
  // Slug Helpers
  // ============================================================
  const createSlug = useCallback((name: string, id: number) => {
    const slugifiedName = slugify(name, { lower: true });
    return `${slugifiedName}-${id}`;
  }, []);

  const extractIdFromSlug = useCallback((slug: string): number | undefined => {
    const id = slug.split('-').pop();
    const numericId = Number(id);
    if (Number.isSafeInteger(numericId) && numericId > 0) {
      return numericId;
    }
    return undefined;
  }, []);

  // ============================================================
  // Parse URL Values
  // ============================================================
  const companyIdFromUrl = useMemo(() => {
    const companyParam = parsedQuery.company as string | undefined;
    if (!companyParam) {
      return undefined;
    }
    const companyId = extractIdFromSlug(companyParam);
    if (!companyId || !isFirmPage || companyList === undefined) {
      return companyId;
    }
    if (companyList === null) {
      return undefined;
    }
    return companyList.some(company => company.company_id === companyId && isFirmWaterfallCompany(company))
      ? companyId
      : undefined;
  }, [parsedQuery.company, extractIdFromSlug, isFirmPage, companyList]);

  const hasUnavailableCompanyFromUrl = useMemo(
    () =>
      isFirmPage &&
      typeof parsedQuery.company === 'string' &&
      companyList != null &&
      companyIdFromUrl === undefined,
    [companyIdFromUrl, companyList, isFirmPage, parsedQuery.company]
  );

  const measurementDateIdFromUrl = useMemo(() => {
    const dateParam = parsedQuery.date as string | undefined;
    if (!dateParam) {
      return undefined;
    }
    // Format is "YYYY-MM-DD-{id}" or just "YYYY-MM-DD" (date-only, no ID)
    // Check if the last segment after splitting by '-' is a valid ID (not part of the date)
    const parts = dateParam.split('-');
    if (parts.length === 4) {
      // Format: YYYY-MM-DD-{id}
      return extractIdFromSlug(dateParam);
    }
    // Format: YYYY-MM-DD (date-only, used during company transition)
    return undefined;
  }, [parsedQuery.date, extractIdFromSlug]);

  const measurementDateValueFromUrl = useMemo(() => {
    const dateParam = parsedQuery.date as string | undefined;
    if (!dateParam) {
      return undefined;
    }
    // Extract just the YYYY-MM-DD portion
    const parts = dateParam.split('-');
    if (parts.length >= 3) {
      // Take first 3 parts (YYYY, MM, DD)
      const dateValue = `${parts[0]}-${parts[1]}-${parts[2]}`;
      const parsed = moment(dateValue, CALENDAR_DATE_FORMAT);
      if (parsed.isValid()) {
        return dateValue;
      }
    }
    return undefined;
  }, [parsedQuery.date]);

  const captableIdFromUrl = useMemo(() => {
    const captableParam = parsedQuery[WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION] as string | undefined;
    if (!captableParam) {
      return undefined;
    }
    return extractIdFromSlug(captableParam);
  }, [extractIdFromSlug, parsedQuery]);

  const exitDateFromUrl = useMemo(() => {
    const exitDateParam = parsedQuery[WATERFALL_QUERY_PARAM_EXIT_DATE] as string | undefined;
    if (!exitDateParam) {
      return undefined;
    }
    const parsed = moment(exitDateParam, 'YYYY-MM-DD');
    if (parsed.isValid()) {
      return parsed.format(MOMENT_DEFAULT_DATE_FORMAT);
    }
    return undefined;
  }, [parsedQuery]);

  const equityValueFromUrl = useMemo(() => {
    const param = parsedQuery[WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE] as string | undefined;
    if (param === undefined) {
      return undefined;
    }
    const value = Number(param);
    return Number.isFinite(value) ? value : undefined;
  }, [parsedQuery]);

  const enterpriseValueFromUrl = useMemo(() => {
    const param = parsedQuery[WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE] as string | undefined;
    if (param === undefined) {
      return undefined;
    }
    const value = Number(param);
    return Number.isFinite(value) ? value : undefined;
  }, [parsedQuery]);

  const cashOverrideFromUrl = useMemo(() => {
    const param = parsedQuery[WATERFALL_QUERY_PARAM_PLUS_CASH] as string | undefined;
    if (param === undefined) {
      return undefined;
    }
    const value = Number(param);
    return Number.isFinite(value) ? value : undefined;
  }, [parsedQuery]);

  const debtOverrideFromUrl = useMemo(() => {
    const param = parsedQuery[WATERFALL_QUERY_PARAM_LESS_DEBT] as string | undefined;
    if (param === undefined) {
      return undefined;
    }
    const value = Number(param);
    return Number.isFinite(value) ? value : undefined;
  }, [parsedQuery]);

  const foreignCurrencyFromUrl = useMemo(() => {
    const currency = parsedQuery[WATERFALL_QUERY_PARAM_FOREIGN_CURRENCY];
    if (typeof currency !== 'string' || !/^[A-Za-z]{3}$/.test(currency)) {
      return DEFAULT_CURRENCY;
    }
    const normalizedCurrency = currency.toUpperCase();
    return normalizedCurrency in worldCurrencies ? (normalizedCurrency as CurrencyCode) : DEFAULT_CURRENCY;
  }, [parsedQuery]);

  const currencyUnitsFromUrl = useMemo(() => {
    const currencyFormat = parsedQuery[WATERFALL_QUERY_PARAM_CURRENCY_FORMAT];
    if (typeof currencyFormat !== 'string' || !isWaterfallCurrencyFormat(currencyFormat)) {
      return MILLIONS_UNIT;
    }
    return WATERFALL_CURRENCY_FORMAT_TO_UNITS[currencyFormat];
  }, [parsedQuery]);

  const foreignExchangeRateFromUrl = useMemo(() => {
    const param = parsedQuery[WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE];
    if (typeof param !== 'string') {
      return undefined;
    }
    const value = Number(param);
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }, [parsedQuery]);

  // An override without a usable rate is incomplete; fall back to the historical FX query.
  const isForeignExchangeRateOverriddenFromUrl =
    foreignExchangeRateFromUrl != null &&
    parsedQuery[WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_OVERRIDDEN] === 'true';

  const foreignExchangeRateInputCurrencyFromUrl: WaterfallExchangeRateInputCurrency =
    parsedQuery[WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_INPUT_CURRENCY] === 'base' ? 'base' : 'foreign';

  const viewIdFromUrl = useMemo(() => {
    const value = parsedQuery[WATERFALL_QUERY_PARAM_VIEW];
    if (typeof value !== 'string') {
      return undefined;
    }
    const viewId = Number(value);
    return Number.isSafeInteger(viewId) && viewId > 0 ? viewId : undefined;
  }, [parsedQuery]);

  // ============================================================
  // Resolve URL values against Data Lists
  // ============================================================
  const selectedMeasurementDate = useMemo(() => {
    if (!measurementDateValueFromUrl || !companyMeasurementDates?.length) {
      return undefined;
    }
    // Match by date value (YYYY-MM-DD), not by ID
    return companyMeasurementDates.find(md => md.date === measurementDateValueFromUrl);
  }, [measurementDateValueFromUrl, companyMeasurementDates]);

  const selectedCaptable = useMemo(() => {
    if (!captableIdFromUrl || !capTableList?.length) {
      return undefined;
    }
    return capTableList.find(ct => ct.id === captableIdFromUrl);
  }, [captableIdFromUrl, capTableList]);

  // ============================================================
  // Update Functions
  // ============================================================
  const setCompany = useCallback(
    (companyId: number, companyName?: string) => {
      if (!isFirmPage || companyIdFromUrl === companyId) {
        return;
      } // Company pages don't set company in URL
      const name = companyName || companyList?.find(c => c.company_id === companyId)?.company_name || 'company';
      updateUrlParams({
        company: createSlug(name, companyId),
        [WATERFALL_QUERY_PARAM_VIEW]: undefined,
        ...CLEARED_CASH_AND_DEBT_OVERRIDE_PARAMS,
        ...CLEARED_FOREIGN_EXCHANGE_RATE_PARAMS,
      });
    },
    [companyIdFromUrl, isFirmPage, companyList, createSlug, updateUrlParams]
  );

  const setMeasurementDate = useCallback(
    (measurementDate: { date: string }, options?: SetWaterfallMeasurementDateOptions) => {
      if (!measurementDate.date) {
        return;
      }
      const dateValue = moment(measurementDate.date).format('YYYY-MM-DD');
      if (measurementDateValueFromUrl === dateValue) {
        return;
      }
      updateUrlParams({
        date: dateValue,
        ...(options?.clearCaptable ? { [WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION]: undefined } : {}),
        ...CLEARED_CASH_AND_DEBT_OVERRIDE_PARAMS,
        ...CLEARED_FOREIGN_EXCHANGE_RATE_PARAMS,
      });
    },
    [measurementDateValueFromUrl, updateUrlParams]
  );

  const setCaptable = useCallback(
    (captable: { id?: number; name?: string }) => {
      if (!captable.id) {
        return;
      }
      if (captableIdFromUrl === captable.id) {
        return;
      }
      const slug = createSlug(captable.name || 'captable', captable.id);
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION]: slug,
        ...CLEARED_CASH_AND_DEBT_OVERRIDE_PARAMS,
        ...CLEARED_FOREIGN_EXCHANGE_RATE_PARAMS,
      });
    },
    [captableIdFromUrl, createSlug, updateUrlParams]
  );

  const setExitDate = useCallback(
    (date: string) => {
      const parsedDate = moment(date, [MOMENT_DEFAULT_DATE_FORMAT, ISO_DATE_FORMAT], true);
      if (!parsedDate.isValid()) {
        return;
      }
      const formatted = parsedDate.format(ISO_DATE_FORMAT);
      if (parsedQuery[WATERFALL_QUERY_PARAM_EXIT_DATE] === formatted) {
        return;
      }
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_EXIT_DATE]: formatted,
        ...CLEARED_FOREIGN_EXCHANGE_RATE_PARAMS,
      });
    },
    [parsedQuery, updateUrlParams]
  );

  const setEquityValue = useCallback(
    (value: number) => {
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE]: toString(value),
        [WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE]: undefined, // Clear enterprise when setting equity
      });
    },
    [updateUrlParams]
  );

  const setEnterpriseValue = useCallback(
    (value: number) => {
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_EXIT_ENTERPRISE_VALUE]: toString(value),
        [WATERFALL_QUERY_PARAM_EXIT_EQUITY_VALUE]: undefined, // Clear equity when setting enterprise
      });
    },
    [updateUrlParams]
  );

  const setCash = useCallback(
    (value: number | null) => {
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_PLUS_CASH]: isNil(value) ? undefined : toString(value),
      });
    },
    [updateUrlParams]
  );

  const setDebt = useCallback(
    (value: number | null) => {
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_LESS_DEBT]: isNil(value) ? undefined : toString(value),
      });
    },
    [updateUrlParams]
  );

  const setForeignCurrency = useCallback(
    (currency: CurrencyCode) => {
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_FOREIGN_CURRENCY]: String(currency).toUpperCase(),
        ...CLEARED_FOREIGN_EXCHANGE_RATE_PARAMS,
      });
    },
    [updateUrlParams]
  );

  const setCurrencyUnits = useCallback(
    (units: Units) => {
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_CURRENCY_FORMAT]: WATERFALL_UNITS_TO_CURRENCY_FORMAT[units],
      });
    },
    [updateUrlParams]
  );

  const setForeignExchangeRate = useCallback(
    (value: number, isOverridden: boolean, inputCurrency: WaterfallExchangeRateInputCurrency = 'foreign') => {
      updateUrlParams({
        [WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE]: toString(value),
        [WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_OVERRIDDEN]: isOverridden ? 'true' : undefined,
        [WATERFALL_QUERY_PARAM_FOREIGN_EXCHANGE_RATE_INPUT_CURRENCY]: isOverridden ? inputCurrency : undefined,
      });
    },
    [updateUrlParams]
  );

  const clearView = useCallback(() => {
    updateUrlParams({ [WATERFALL_QUERY_PARAM_VIEW]: undefined });
  }, [updateUrlParams]);

  const ensureCurrencyDefaults = useCallback(() => {
    const defaults: Record<string, string | undefined> = {};
    if (!parsedQuery[WATERFALL_QUERY_PARAM_FOREIGN_CURRENCY]) {
      defaults[WATERFALL_QUERY_PARAM_FOREIGN_CURRENCY] = DEFAULT_CURRENCY;
    }
    if (!parsedQuery[WATERFALL_QUERY_PARAM_CURRENCY_FORMAT]) {
      defaults[WATERFALL_QUERY_PARAM_CURRENCY_FORMAT] = WATERFALL_CURRENCY_FORMAT.MILLIONS;
    }
    if (Object.keys(defaults).length > 0) {
      updateUrlParams(defaults);
    }
  }, [parsedQuery, updateUrlParams]);

  // ============================================================
  // Batch Update Functions
  // ============================================================
  const resetForCompanyChange = useCallback(
    (companyId: number, companyName?: string) => {
      // Extract the current date value (YYYY-MM-DD) to preserve during company change
      // This allows us to try to match the same date in the new company's measurement dates
      const currentDateParam = parsedQuery.date as string | undefined;
      let preservedDateValue: string | undefined;
      if (currentDateParam) {
        const parts = currentDateParam.split('-');
        if (parts.length >= 3) {
          preservedDateValue = `${parts[0]}-${parts[1]}-${parts[2]}`;
        }
      }

      const params: Record<string, string | undefined> = {
        // Preserve date value (without ID) for matching in new company, or clear if none
        date: preservedDateValue,
        // Clear cap table (cap tables are specific to company + measurement date)
        [WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION]: undefined,
        // Cash and debt are company-specific; fall back to the new company's financials.
        ...CLEARED_CASH_AND_DEBT_OVERRIDE_PARAMS,
        [WATERFALL_QUERY_PARAM_VIEW]: undefined,
        ...CLEARED_FOREIGN_EXCHANGE_RATE_PARAMS,
        // Preserve exit date and equity/enterprise value.
      };

      // Set new company (firm page only)
      if (isFirmPage) {
        const name = companyName || companyList?.find(c => c.company_id === companyId)?.company_name || 'company';
        params.company = createSlug(name, companyId);
      }

      updateUrlParams(params);
    },
    [isFirmPage, companyList, createSlug, updateUrlParams, parsedQuery.date]
  );

  const setDefaults = useCallback(
    (defaults: {
      measurementDate?: { date: string };
      captable?: { id?: number; name?: string };
      exitDate?: string;
      companyId?: number;
      companyName?: string;
    }) => {
      const params: Record<string, string | undefined> = {};

      // Only set params that are currently missing from URL
      if (defaults.measurementDate?.date && !parsedQuery.date) {
        params.date = moment(defaults.measurementDate.date).format(CALENDAR_DATE_FORMAT);
      }

      if (defaults.captable?.id && !parsedQuery[WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION]) {
        params[WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION] = createSlug(
          defaults.captable.name || 'captable',
          defaults.captable.id
        );
      }

      if (defaults.exitDate && !parsedQuery[WATERFALL_QUERY_PARAM_EXIT_DATE]) {
        params[WATERFALL_QUERY_PARAM_EXIT_DATE] = moment(defaults.exitDate).format(CALENDAR_DATE_FORMAT);
      }

      if (isFirmPage && defaults.companyId && !parsedQuery.company) {
        const name =
          defaults.companyName ||
          companyList?.find(c => c.company_id === defaults.companyId)?.company_name ||
          'company';
        params.company = createSlug(name, defaults.companyId);
      }

      if (Object.keys(params).length > 0) {
        updateUrlParams(params);
      }
    },
    [parsedQuery, isFirmPage, companyList, createSlug, updateUrlParams]
  );

  return {
    // Raw URL values
    companyIdFromUrl,
    hasUnavailableCompanyFromUrl,
    measurementDateIdFromUrl,
    measurementDateValueFromUrl,
    captableIdFromUrl,
    exitDateFromUrl,
    equityValueFromUrl,
    enterpriseValueFromUrl,
    cashOverrideFromUrl,
    debtOverrideFromUrl,
    foreignCurrencyFromUrl,
    currencyUnitsFromUrl,
    foreignExchangeRateFromUrl,
    isForeignExchangeRateOverriddenFromUrl,
    foreignExchangeRateInputCurrencyFromUrl,
    viewIdFromUrl,

    // Resolved values
    selectedMeasurementDate,
    selectedCaptable,

    // Update functions
    setCompany,
    setMeasurementDate,
    setCaptable,
    setExitDate,
    setEquityValue,
    setEnterpriseValue,
    setCash,
    setDebt,
    setForeignCurrency,
    setCurrencyUnits,
    setForeignExchangeRate,
    clearView,
    ensureCurrencyDefaults,

    // Batch updates
    resetForCompanyChange,
    setDefaults,

    // Utility
    parsedQuery,
  };
};
